import { useMutation } from '@tanstack/react-query';
import { useFetcher } from '../../graphql';

export interface CallTokenResult {
  call: {
    id: string;
    room: string;
    state: string;
    caller: { id: string };
    callee: { id: string };
  };
  token: string | null;
  url: string;
}

export interface StartCallArgs {
  calleeId: string;
  /**
   * The Activity the call is being placed from, when it is placed from inside
   * one (the per-message "Call" action). The backend logs the call and its
   * outcome into that Activity's thread.
   */
  issueId?: string;
}

/**
 * Place a call to another account in the caller's workspace. Resolves with the
 * caller's LiveKit token + server url so the caller can join the room and wait
 * for the callee to answer (≈ Slack calls.add).
 */
export const useStartCall = () => {
  const fetcherFn = useFetcher<
    { call: { startCall: CallTokenResult } },
    { input: StartCallArgs }
  >(START_CALL);

  return useMutation({
    mutationFn: async ({
      calleeId,
      issueId,
    }: StartCallArgs): Promise<CallTokenResult> => {
      const data = await fetcherFn({
        // Omitted rather than sent as null: `issueId` is an optional input
        // field, and a call placed outside an Activity has no context to log.
        input: issueId ? { calleeId, issueId } : { calleeId },
      });
      return data.call.startCall;
    },
  });
};

const START_CALL = `
  mutation START_CALL($input: StartCallInput!) {
    call {
      startCall(input: $input) {
        call {
          id
          room
          state
          caller { id }
          callee { id }
        }
        token
        url
      }
    }
  }
`;
